import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { auth } from '../lib/api';
import OfflineBanner from './OfflineBanner';
import DegradedBanner from './DegradedBanner';
import { useApplications, useCompletion } from '../hooks/usePortal';
import { usePortalContext } from '../hooks/usePortalContext';
import Header, { ShellUser } from './shell/Header';
import Sidebar from './shell/Sidebar';
import MobileDrawer from './shell/MobileDrawer';
import BottomNav from './shell/BottomNav';
import Footer from './shell/Footer';
import ProgressBanner from './shell/ProgressBanner';
import { isOnProgram, NavContext } from './shell/navConfig';
import { deepLinkStateFor } from '../lib/steps';

/**
 * Unresolvable context is surfaced, never defaulted: if /me/context failed,
 * or an application exists but its program/brand/season could not be derived,
 * every page shows this banner instead of silently rendering another
 * combination's theming, steps or check-ins.
 */
function ContextBanner() {
  const { ctx, error, loading, reload } = usePortalContext();
  if (loading) return null;
  const unresolved = ctx?.hasApplication ? ctx.unresolved : [];
  if (!error && unresolved.length === 0) return null;
  return (
    <div className="card alertcard" role="alert" style={{ marginBottom: 14 }}>
      <h2>We couldn't load your program details</h2>
      <p>
        {error
          ? `The portal couldn't reach your program data: ${error}`
          : `Your ${unresolved.join(', ')} could not be determined. Some information may be missing until this is resolved.`}
      </p>
      <p style={{ marginTop: 10 }}><button onClick={reload} style={{ width: 'auto' }}>Try again</button></p>
    </div>
  );
}

/**
 * Responsive app shell (PRD §6): fixed header (56px mobile / 64px desktop),
 * 256px primary sidebar on desktop with a collapsible tablet rail, slide-in
 * drawer + 68px bottom navigation on mobile, conditional progress banner and
 * a desktop-only footer. All nav surfaces render from shell/navConfig (§6.3).
 */
export default function AppLayout() {
  const navigate = useNavigate();
  const { active } = useApplications();
  const { ctx, theme, programConfig } = usePortalContext();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navCtx: NavContext = { onProgram: isOnProgram(active?.status) };

  // One CACHED journey fetch for the whole shell: drives the progress banner
  // and is the same cache entry the dashboard reads — one network call total.
  // useCompletion also derives the two-stage completion signal (references are
  // fetched only once everything else is done, so it's free mid-application).
  const { done: doneCount, total: totalSteps, pct, nextStep, stage } = useCompletion(active?.id);

  // One-time celebration toast on the transition into each completion stage,
  // fired from the always-mounted shell so it surfaces on any page. Persisted
  // per (application, stage) so it never re-fires on a refetch or revisit.
  const celebratedRef = useRef('');
  useEffect(() => {
    if (!active?.id || (stage !== 'complete' && stage !== 'awaiting_references')) return;
    const key = `pb-celebrated:${active.id}:${stage}`;
    if (celebratedRef.current === key || localStorage.getItem(key) === '1') return;
    celebratedRef.current = key;
    localStorage.setItem(key, '1');
    toast.success(
      stage === 'complete'
        ? '🎉 Your application is complete!'
        : "🎉 You've done your part — we're collecting your references.",
      { duration: 6000 },
    );
  }, [active?.id, stage]);

  // Program + season from the RESOLVED context (Globus program config / season
  // catalogue), falling back to the application row's enrichment — same live
  // source, never a hardcoded name.
  const programName = programConfig?.config?.name ?? ctx?.program?.name ?? active?.programName;
  const season = ctx?.season?.name ?? undefined;

  // Brand identity from the RESOLVED brand (Globus /branding via BFF) — the
  // neutral "AA / Participant Portal" shell only shows while unresolved.
  const brandName = ctx?.brand?.name ?? 'Participant Portal';
  const logo = theme?.logoUrl ? (
    <img className="logo" src={theme.logoUrl} alt="" style={{ objectFit: 'contain', background: '#fff' }} />
  ) : (
    <span className="logo" aria-hidden>AA</span>
  );

  const user: ShellUser = useMemo(() => {
    const first = ctx?.preferredName ?? active?.preferredName ?? ctx?.firstName ?? active?.firstName ?? '';
    const last = ctx?.lastName ?? active?.lastName ?? '';
    const name = `${first} ${last}`.trim() || 'Applicant';
    const initials = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || 'AA';
    return { name, email: ctx?.email ?? active?.email, initials, avatarUrl: ctx?.profileImageUrl ?? undefined };
  }, [ctx, active]);

  const signOut = async () => {
    await auth.signOut();
    navigate('/sign-in');
  };

  return (
    <div className="shell">
      <a className="skiplink" href="#main">Skip to main content</a>

      <Header
        brandName={brandName}
        programName={programName}
        season={season}
        logo={logo}
        user={user}
        navCtx={navCtx}
        onOpenDrawer={() => setDrawerOpen(true)}
        onSignOut={signOut}
      />

      {active && totalSteps > 0 && (
        <ProgressBanner
          applicationId={active.id}
          pct={pct}
          done={doneCount}
          total={totalSteps}
          stage={stage}
          nextStep={nextStep && { title: nextStep.title, ...deepLinkStateFor(nextStep) }}
        />
      )}

      <div className="body">
        <Sidebar user={user} navCtx={navCtx} onSignOut={signOut} />
        <main id="main" className="content" tabIndex={-1}>
          <OfflineBanner />
          <DegradedBanner />
          <ContextBanner />
          <Outlet />
        </main>
      </div>

      <Footer />

      <BottomNav navCtx={navCtx} />

      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        brandName={brandName}
        logo={logo}
        user={user}
        navCtx={navCtx}
        onSignOut={signOut}
      />
    </div>
  );
}
